import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import mixpanel from "mixpanel-browser";

import { selectAllTransactions } from "store/payments/selectors";

import { selectUser } from "store/users/selectors";
import { actions as userActions } from "store/users";
import { fetchTransactions } from "store/payments/thunk";

import Filters from "./components/Filters";
import Table from "./components/Table";
import LegacyTransfer from "./components/LegacyTransfer";

import classes from "./styles.module.scss";

const { fetchUserData } = userActions;

const Transactions = ({ fetchTransactions, fetchUserData, transactions, user }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [filters, setFilters] = useState({
        type: ["PURCHASED", "TRANSFER_IN", "TRANSFER_OUT", "SPENT.INSIGHTS", "SPENT.PARAMETRIC", "SPENT.COMMUNITY"],
        dateRange: { valueForDropdown: "allTime", start: null, end: null },
        sortBy: { value: "time", label: "Time" },
    });
    const [lazyLoadData, setLazyLoadData] = useState({
        hasMore: false,
        lastId: null,
        isLoading: false,
    });

    const { uid = "" } = user || {};

    useEffect(() => {
        if (!uid) return;
        mixpanel.track("Transactions Page View", {
            UserId: uid,
        });
    }, [uid]);

    useEffect(() => {
        if (uid && !isLoading) {
            (async () => {
                setIsLoading(true);

                const [result] = await Promise.all([fetchTransactions(uid, filters, null), fetchUserData(uid)]);

                if (result) {
                    setLazyLoadData({
                        hasMore: result.hasMore,
                        lastId: result.lastId,
                        isLoading: false,
                    });
                }

                setIsLoading(false);
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchTransactions, fetchUserData, uid, filters]);

    const fetchMoreTransactions = async (isLoadingMore) => {
        if (lazyLoadData.hasMore && !lazyLoadData.isLoading && !isLoading) {
            setLazyLoadData((prev) => ({ ...prev, isLoading: true }));

            const result = await fetchTransactions(uid, filters, lazyLoadData.lastId, isLoadingMore);

            if (result) {
                setLazyLoadData({
                    hasMore: result.hasMore,
                    lastId: result.lastId,
                    isLoading: false,
                });
            }
        }
    };

    const changeFilters = (filtersToUpdate) => {
        setFilters((prevFilters) => ({ ...prevFilters, ...filtersToUpdate }));
        setLazyLoadData({
            hasMore: false,
            lastId: null,
            isLoading: false,
        });
    };

    const refreshTransactions = async () => {
        if (!uid) return;

        setIsLoading(true);
        setLazyLoadData({
            hasMore: false,
            lastId: null,
            isLoading: false,
        });

        const result = await fetchTransactions(uid, filters, null);

        if (result) {
            setLazyLoadData({
                hasMore: result.hasMore,
                lastId: result.lastId,
                isLoading: false,
            });
        }

        setIsLoading(false);
    };

    return (
        <div className={classes.transactionsContainer}>
            <Filters
                filters={filters}
                setFilters={changeFilters}
                setLastId={() =>
                    setLazyLoadData({
                        hasMore: false,
                        lastId: null,
                        isLoading: false,
                    })
                }
            />
            <LegacyTransfer onTransferSuccess={refreshTransactions} />
            <Table
                filters={filters}
                setFilters={setFilters}
                fetchMoreTransactions={fetchMoreTransactions}
                lazyLoadData={lazyLoadData}
                loading={isLoading}
            />
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    transactions: selectAllTransactions,
    user: selectUser,
});

const mapDispatchToProps = (dispatch) => ({
    fetchTransactions: (uid, filters, lastId, isLoadingMore) =>
        dispatch(fetchTransactions(uid, filters, lastId, isLoadingMore)),
    fetchUserData: (uid) => dispatch(fetchUserData(uid)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Transactions);
